import React, { ReactNode } from 'react';

import { FlexLayout } from '@zapier/common-components';
import {
  Button,
  Colors,
  Icon,
  Margin,
  SkeletonBlock,
  SkeletonLoader,
  Surface,
  Text,
} from '@zapier/design-system';

import { Price } from '@zapier/design-system-revenue/src/components/other';
import { useSize } from '@zapier/design-system-revenue/src/utils';

import { useData as _useData } from './hooks/useData';

type LoadingWrapperProps = {
  children: ReactNode;
  isLoading: boolean;
};

const LoadingWrapper = ({ children, isLoading }: LoadingWrapperProps) => {
  const content = isLoading ? (
    <SkeletonLoader>
      <SkeletonBlock height={72} width="100%" />
    </SkeletonLoader>
  ) : (
    children
  );

  return <Margin margin="20px 0">{content}</Margin>;
};

const buttonColorThemeBase = {
  backgroundColor: Colors.grey100,
  color: Colors.blue500,
};

const buttonColorThemeActive = {
  ...buttonColorThemeBase,
  textDecoration: 'underline',
};

const buttonColorTheme = {
  custom: {
    base: buttonColorThemeBase,
    disabled: {},
    hover: buttonColorThemeActive,
    focus: buttonColorThemeActive,
  },
};

type Props = {
  /** For dependency injection testing only */
  useData?: () => ReturnType<typeof _useData>;
};

/**
 * Allows the user to toggle on and off auto-upgrade for their premium (paid, non-Team, non-Company) trial.
 */
export const AutoUpgradeMessageBar = ({ useData = _useData }: Props) => {
  const {
    handleToggleAutoUpgrade,
    hasPaymentSource,
    isAutoUpgradeEnabled,
    isLoading,
    isOnExtendedTrial,
    nextPlanAmount,
    trialDaysLeft,
  } = useData();

  const size = useSize();

  return (
    <LoadingWrapper isLoading={isLoading}>
      {isOnExtendedTrial && hasPaymentSource ? (
        <Surface
          backgroundColor="grey100"
          borderColor="grey200"
          shadow="elevation5"
        >
          <div style={{ padding: '15px 10px' }}>
            <FlexLayout
              align="center"
              direction={size === 'small' ? 'column' : 'row'}
              wrap={size === 'small' ? 'wrap' : 'nowrap'}
            >
              <FlexLayout align="center" gutter="extraSmall" wrap="nowrap">
                <Icon name="alertInfo" size={32} />

                <Text color="grey900" type="altText900">
                  Yay! You're experiencing a{' '}
                  <a
                    href="/app/billing/plans"
                    style={{ color: Colors.blue500 }}
                  >
                    premium Zapier
                  </a>{' '}
                  account for free for the next {trialDaysLeft} days. After
                  that, you'll{' '}
                  {isAutoUpgradeEnabled ? (
                    <span>
                      be charged
                      {nextPlanAmount > -1 && (
                        <span>
                          {' '}
                          <Price>{nextPlanAmount}</Price>/month
                        </span>
                      )}
                    </span>
                  ) : (
                    <span>lose premium features</span>
                  )}
                  .
                </Text>
              </FlexLayout>

              <Button
                color="custom"
                colorTheme={buttonColorTheme}
                onClick={handleToggleAutoUpgrade}
                size="small"
              >
                <Text color="blue500" type="altText900">
                  {isAutoUpgradeEnabled
                    ? 'Disable Auto-Upgrade'
                    : 'Enable Auto-Upgrade'}
                </Text>
              </Button>
            </FlexLayout>
          </div>
        </Surface>
      ) : null}
    </LoadingWrapper>
  );
};
